"use client";

import React, { createContext, useContext, useState, useEffect } from 'react';

// Create the context
const UserContext = createContext();

// Custom hook to use the user context
export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};

// Provider component
export function UserProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Initialize user data from localStorage on component mount
  useEffect(() => {
    try {
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        setUser(JSON.parse(storedUser));
      }
    } catch (error) {
      console.error('Error loading user data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Save user data to localStorage whenever it changes
  useEffect(() => {
    if (user) {
      localStorage.setItem('user', JSON.stringify(user));
    }
  }, [user]);

  // Authentication functions
  const login = async (email, password) => {
    setLoading(true);
    setError(null);
    
    try {
      // This is a mock login - replace with actual API call
      // In a real app, you would make an API call here
      // const response = await fetch('/api/login', {
      //   method: 'POST',
      //   headers: { 'Content-Type': 'application/json' },
      //   body: JSON.stringify({ email, password }),
      // });
      // const data = await response.json();
      
      // Mock successful response
      const mockUser = {
        id: '123',
        name: 'John Doe',
        email: email,
        phone: '',
        addresses: [],
        credits: 150,
        avatar: null,
      };
      
      setUser(mockUser);
      return true;
    } catch (err) {
      setError('Login failed. Please check your credentials and try again.');
      return false;
    } finally {
      setLoading(false);
    }
  };

  const signup = async (name, email, password) => {
    setLoading(true);
    setError(null);
    
    try {
      // This is a mock signup - replace with actual API call
      // const response = await fetch('/api/signup', {
      //   method: 'POST',
      //   headers: { 'Content-Type': 'application/json' },
      //   body: JSON.stringify({ name, email, password }),
      // });
      // const data = await response.json();
      
      // Mock successful response
      const mockUser = {
        id: '123',
        name: name,
        email: email,
        phone: '',
        addresses: [],
        credits: 150,
        avatar: null,
      };
      
      setUser(mockUser);
      return true;
    } catch (err) {
      setError('Signup failed. Please try again.');
      return false;
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem('user');
    setUser(null);
  };

  // Profile update functions
  const updateProfile = (updatedData) => {
    setUser(prevUser => ({
      ...prevUser,
      ...updatedData,
    }));
  };
  
  const addAddress = (newAddress) => {
    setUser(prevUser => ({
      ...prevUser,
      addresses: [...(prevUser.addresses || []), newAddress],
    }));
  };
  
  const updateAddress = (addressId, updatedAddress) => {
    setUser(prevUser => ({
      ...prevUser,
      addresses: prevUser.addresses.map(addr => 
        addr.id === addressId ? { ...addr, ...updatedAddress } : addr
      ),
    }));
  };
  
  const deleteAddress = (addressId) => {
    setUser(prevUser => ({
      ...prevUser,
      addresses: prevUser.addresses.filter(addr => addr.id !== addressId),
    }));
  };
  
  const setDefaultAddress = (addressId) => {
    setUser(prevUser => ({
      ...prevUser,
      addresses: prevUser.addresses.map(addr => ({
        ...addr,
        isDefault: addr.id === addressId
      })),
    }));
  };

  // Password management function
  const changePassword = async (currentPassword, newPassword) => {
    setLoading(true);
    setError(null);
    
    try {
      // This is a mock password change - replace with actual API call
      // const response = await fetch('/api/change-password', {
      //   method: 'POST',
      //   headers: { 'Content-Type': 'application/json' },
      //   body: JSON.stringify({ 
      //     userId: user.id,
      //     currentPassword,
      //     newPassword 
      //   }),
      // });
      
      // Mock successful response
      // In a real app, you would validate the current password on the server
      
      return true;
    } catch (err) {
      setError('Failed to change password. Please try again.');
      return false;
    } finally {
      setLoading(false);
    }
  };

  const value = {
    user,
    loading,
    error,
    isAuthenticated: !!user,
    login,
    signup,
    logout,
    updateProfile,
    addAddress,
    updateAddress,
    deleteAddress,
    setDefaultAddress,
    changePassword,
  };

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
} 